import { useState, useMemo } from 'react'
import type { DimensionId, TypeCode } from '@mindtype/shared'
import { ALL_TYPE_CODES, getTypeInterpretation, DIMENSION_INFO, TYPE_COLORS, GROUP_COLORS } from '@mindtype/shared'
import { TypeDetailModal } from './TypeDetailModal'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface TypeBrowserProps {
  userType?: string
  colorConfig?: ColorConfig
}

const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

type FilterValue = 'all' | 'A' | 'R' | 'S' | 'C' | 'I' | 'E' | 'D' | 'O'

const DIMENSION_FILTERS: { dimension: DimensionId; options: { value: FilterValue; label: string }[] }[] = [
  {
    dimension: 'D1',
    options: [
      { value: 'all', label: '全部' },
      { value: 'A', label: 'A 主动型' },
      { value: 'R', label: 'R 响应型' },
    ],
  },
  {
    dimension: 'D2',
    options: [
      { value: 'all', label: '全部' },
      { value: 'S', label: 'S 系统型' },
      { value: 'C', label: 'C 情境型' },
    ],
  },
  {
    dimension: 'D3',
    options: [
      { value: 'all', label: '全部' },
      { value: 'I', label: 'I 内省型' },
      { value: 'E', label: 'E 外显型' },
    ],
  },
  {
    dimension: 'D4',
    options: [
      { value: 'all', label: '全部' },
      { value: 'D', label: 'D 直觉型' },
      { value: 'O', label: 'O 观察型' },
    ],
  },
]

export function TypeBrowser({ userType, colorConfig = DEFAULT_COLOR }: TypeBrowserProps) {
  const color = colorConfig
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [filters, setFilters] = useState<Record<DimensionId, FilterValue>>({
    D1: 'all',
    D2: 'all',
    D3: 'all',
    D4: 'all',
  })

  const filteredTypes = useMemo(() => {
    return ALL_TYPE_CODES.filter(code => {
      if (filters.D1 !== 'all' && code[0] !== filters.D1) return false
      if (filters.D2 !== 'all' && code[1] !== filters.D2) return false
      if (filters.D3 !== 'all' && code[2] !== filters.D3) return false
      if (filters.D4 !== 'all' && code[3] !== filters.D4) return false
      return true
    })
  }, [filters])

  const setFilter = (dimension: DimensionId, value: FilterValue) => {
    setFilters(prev => ({ ...prev, [dimension]: value }))
  }

  const clearFilters = () => {
    setFilters({ D1: 'all', D2: 'all', D3: 'all', D4: 'all' })
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== 'all')

  return (
    <div
      className="bg-white rounded-xl p-6"
      style={{ border: `1px solid ${color.primary}15` }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">探索所有类型</h2>
        <span className="text-sm" style={{ color: color.primary }}>{filteredTypes.length} / 16 类型</span>
      </div>

      {/* 筛选器 */}
      <div className="space-y-3 mb-6">
        {DIMENSION_FILTERS.map(({ dimension, options }) => {
          const dimInfo = DIMENSION_INFO[dimension]
          return (
            <div key={dimension} className="flex items-center gap-3">
              <span className="w-20 text-sm text-gray-600 flex-shrink-0">
                {dimInfo.name}
              </span>
              <div className="flex gap-2 flex-wrap">
                {options.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setFilter(dimension, opt.value)}
                    className="px-3 py-1.5 text-sm rounded-lg transition-colors"
                    style={{
                      backgroundColor: filters[dimension] === opt.value ? color.primary : `${color.primary}10`,
                      color: filters[dimension] === opt.value ? color.text : color.primary,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )
        })}

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm underline"
            style={{ color: color.primary }}
          >
            清除筛选
          </button>
        )}
      </div>

      {/* 类型网格 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {filteredTypes.map(code => {
          const interpretation = getTypeInterpretation(code)
          const isUser = code === userType
          const colorConfig = TYPE_COLORS[code as TypeCode]
          const groupColors = GROUP_COLORS[colorConfig.group]

          return (
            <button
              key={code}
              onClick={() => setSelectedType(code)}
              className={`relative p-4 rounded-xl text-left transition-all hover:shadow-md border-l-4 ${
                isUser ? 'ring-2 ring-offset-1' : ''
              }`}
              style={{
                borderLeftColor: groupColors.primary,
                backgroundColor: isUser ? `${groupColors.primary}20` : `${groupColors.primary}10`,
                borderTop: '1px solid #e5e7eb',
                borderRight: '1px solid #e5e7eb',
                borderBottom: '1px solid #e5e7eb',
                ...(isUser ? { '--tw-ring-color': groupColors.primary } as React.CSSProperties : {}),
              }}
            >
              {isUser && (
                <span
                  className="absolute -top-2 -right-2 px-2 py-0.5 text-white text-xs rounded-full"
                  style={{ backgroundColor: colorConfig.primary }}
                >
                  你
                </span>
              )}
              <div
                className="text-xl font-mono font-bold mb-1"
                style={{ color: groupColors.primary }}
              >
                {code}
              </div>
              <div className="text-sm font-medium text-gray-800 mb-1">
                {interpretation?.name}
              </div>
              <div className="text-xs text-gray-500 line-clamp-2">
                {interpretation?.tagline}
              </div>
            </button>
          )
        })}
      </div>

      {filteredTypes.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          没有匹配的类型，请调整筛选条件
        </div>
      )}

      {/* 类型详情模态框 */}
      <TypeDetailModal
        typeCode={selectedType || ''}
        isOpen={!!selectedType}
        onClose={() => setSelectedType(null)}
        isUserType={selectedType === userType}
      />
    </div>
  )
}
